/**
 * TextUtils - Utility functions for text formatting and manipulation.
 *
 * This module provides helpful functions for consistent text formatting
 * across the application, with a focus on security and proper display.
 */

/**
 * Formats text for display while preserving line breaks
 * and preventing XSS attacks by avoiding raw HTML
 *
 * @param {string} text - The text to format
 * @returns {Array} - Array of line objects with content and metadata
 */
export function formatMultilineText(text) {
  if (!text) return [];

  // Trim and split the text by newlines
  return text
    .trim()
    .split("\n")
    .map((line, index) => ({
      content: line,
      index,
      isLineBreakBefore: index > 0
    }));
}

/**
 * Truncates text to a specified length with ellipsis
 *
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} - Truncated text with ellipsis if needed
 */
export function truncateText(text, maxLength = 100) {
  if (!text || text.length <= maxLength) return text || "";

  return text.substring(0, maxLength) + "...";
}

/**
 * Removes HTML tags from text for safe display
 *
 * @param {string} text - The text that might contain HTML
 * @returns {string} - Text with HTML tags removed
 */
export function stripHtml(text) {
  if (!text) return "";

  // Create a temporary div to use browser's HTML parsing
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = text;

  // Return just the text content
  return tempDiv.textContent || tempDiv.innerText || "";
}

/**
 * Normalizes whitespace in text by:
 * - Trimming leading/trailing whitespace
 * - Replacing multiple spaces with a single space
 * - Preserving line breaks
 *
 * @param {string} text - The text to normalize
 * @returns {string} - Normalized text
 */
export function normalizeWhitespace(text) {
  if (!text) return "";

  return text
    .trim()
    .replace(/[ \t]+/g, " ") // Replace multiple spaces/tabs with a single space
    .replace(/\n{3,}/g, "\n\n"); // Replace 3+ consecutive line breaks with just 2
}

/**
 * Converts plain text with URLs to clickable links
 * Note: This should only be used when displaying text in a controlled environment
 *
 * @param {string} text - The plain text that may contain URLs
 * @returns {Array} - Array of objects representing text segments or links
 */
export function linkifyText(text) {
  if (!text) return [];

  // Simple regex for URLs starting with http/https
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  // Split the text into segments
  const segments = [];
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    // Add text before the URL
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        content: text.substring(lastIndex, match.index)
      });
    }

    // Add the URL
    segments.push({
      type: "link",
      content: match[1],
      url: match[1]
    });

    lastIndex = match.index + match[1].length;
  }

  // Add any remaining text
  if (lastIndex < text.length) {
    segments.push({
      type: "text",
      content: text.substring(lastIndex)
    });
  }

  return segments;
}
